import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { allTraitKeys } from "@/lib/breedingValueTraits";

export interface BreedingValueFilterEntry {
  value: string;
  variance: string;
}

interface UseMarketplaceListingsProps {
  searchTerm?: string;
  selectedBreed?: string;
  selectedLocation?: string;
  locationCoordinates?: [number, number] | null;
  rangeKm?: number;
  locationType?: string | null;
  selectedType?: string;
  page?: number;
  limit?: number;
  priceMin?: string;
  priceMax?: string;
  breedingValueFilters?: Record<string, BreedingValueFilterEntry>;
}

// Haversine distance in km
const calculateDistance = (
  lat1: number, lon1: number, lat2: number, lon2: number
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const useMarketplaceListings = ({
  searchTerm = "",
  selectedBreed = "",
  selectedLocation = "",
  locationCoordinates = null,
  rangeKm = 50,
  locationType = null,
  selectedType = "all",
  page = 1,
  limit = 12,
  priceMin = "",
  priceMax = "",
  breedingValueFilters = {},
}: UseMarketplaceListingsProps) => {
  return useQuery({
    queryKey: [
      "marketplace-listings",
      searchTerm, selectedBreed, selectedLocation, locationCoordinates,
      rangeKm, locationType, selectedType, page, priceMin, priceMax,
      breedingValueFilters,
    ],
    queryFn: async () => {
      // Build select with all breeding value columns
      const bvColumns = allTraitKeys.join(",\n          ");

      let query = (supabase
        .from("listings")
        .select(
          `
          id, title, price, breed, type, location, images,
          price_currency, breeder_id, status, created_at,
          location_coordinates, listing_type,
          ${bvColumns}
        `,
          { count: "exact" }
        ) as any)
        .eq("status", "active")
        .is("announcement_id", null)
        .order("created_at", { ascending: false });

      // Text filters
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,breed.ilike.%${searchTerm}%`);
      }
      if (selectedBreed) {
        query = query.eq("breed", selectedBreed);
      }
      if (selectedType !== "all") {
        query = query.ilike("type", `%${selectedType}%`);
      }

      // Price server-side filters
      const pMin = priceMin ? parseFloat(priceMin) : NaN;
      const pMax = priceMax ? parseFloat(priceMax) : NaN;
      if (!isNaN(pMin)) query = query.gte("price", pMin);
      if (!isNaN(pMax)) query = query.lte("price", pMax);

      const { data, error, count } = await query;

      if (error) {
        console.error("Error fetching marketplace listings:", error);
        throw error;
      }

      if (!data || data.length === 0) {
        return { listings: [], totalCount: count || 0 };
      }

      // Location filtering (client-side)
      let filteredData = data;
      if (selectedLocation) {
        const isBroadArea = locationType === "country" || locationType === "region";
        if (isBroadArea) {
          const locationParts = selectedLocation.split(",").map(p => p.trim().toLowerCase());
          filteredData = data.filter(listing => {
            if (!listing.location) return false;
            const loc = listing.location.toLowerCase();
            return locationParts.some(part => loc.includes(part));
          });
        } else if (locationCoordinates) {
          const [searchLng, searchLat] = locationCoordinates;
          filteredData = data.filter(listing => {
            if (!listing.location_coordinates) return false;
            try {
              let lng: number, lat: number;
              if (typeof listing.location_coordinates === "string") {
                const c = JSON.parse(listing.location_coordinates);
                lng = c.longitude; lat = c.latitude;
              } else {
                const c = listing.location_coordinates as any;
                if (c.longitude && c.latitude) { lng = c.longitude; lat = c.latitude; }
                else if (Array.isArray(c)) { [lng, lat] = c; }
                else if (c.coordinates && Array.isArray(c.coordinates)) { [lng, lat] = c.coordinates; }
                else return false;
              }
              return calculateDistance(searchLat, searchLng, lat, lng) <= rangeKm;
            } catch { return false; }
          });
        }
      }

      // Breeding value filtering (client-side)
      const activeBVFilters = Object.entries(breedingValueFilters).filter(
        ([, f]) => f.value !== ""
      );
      if (activeBVFilters.length > 0) {
        filteredData = filteredData.filter(listing => {
          return activeBVFilters.every(([key, filter]) => {
            const listingVal = (listing as any)[key];
            if (listingVal === null || listingVal === undefined || listingVal === "") return true;
            const lv = parseFloat(String(listingVal));
            const fv = parseFloat(filter.value);
            const variance = filter.variance ? parseFloat(filter.variance) : 0;
            if (isNaN(lv) || isNaN(fv)) return false;
            return lv >= fv - variance && lv <= fv + variance;
          });
        });
      }

      // Pagination
      const totalFilteredCount = filteredData.length;
      const from = (page - 1) * limit;
      const paginatedData = filteredData.slice(from, from + limit);

      // Fetch breeder profiles
      const breederIds = [...new Set(paginatedData.map((l: any) => l.breeder_id))] as string[];
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, business_name, first_name, last_name")
        .in("id", breederIds);

      if (profilesError) throw profilesError;

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      const transformedData = paginatedData.map(listing => {
        const profile = profileMap.get(listing.breeder_id);
        let imageUrl = "https://images.unsplash.com/photo-1493962853295-0fd70327578a?w=400&h=300&fit=crop";
        if (listing.images && Array.isArray(listing.images) && listing.images.length > 0) {
          imageUrl = String(listing.images[0]);
        }
        return {
          id: listing.id,
          title: listing.title,
          image: imageUrl,
          price: listing.price
            ? `${listing.price_currency || "$"} ${listing.price.toLocaleString()}`
            : undefined,
          location: listing.location || "Location not specified",
          breeder:
            profile?.business_name ||
            `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() ||
            "Unknown Breeder",
          breed: listing.breed,
          type: listing.type,
          listing_type: listing.listing_type,
          evaluationData: {
            calvingEase: listing.calving_ease,
            birthWeight: listing.birth_weight,
            weaningWeight: listing.weaning_weight,
            milk: listing.milk,
          },
        };
      });

      return { listings: transformedData, totalCount: totalFilteredCount };
    },
  });
};
